import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../firebase/useAuth';
import { CATEGORIES } from '../data/categories';
import FilterBar from '../components/FilterBar';
import FeedResponseCard from '../components/FeedResponseCard';
import styles from './FeedScreen.module.css';

export default function FeedScreen({ initialPersonFilter }) {
  const { state } = useApp();
  const { user } = useAuth();
  const { friends, friendEntries, entries } = state;

  const [activeCategory, setActiveCategory] = useState(null);
  const [activePeople, setActivePeople] = useState(
    () => initialPersonFilter ? new Set([initialPersonFilter]) : new Set()
  );

  const handlePersonClick = (userId) => {
    setActivePeople(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const activatePerson = (userId) => {
    setActivePeople(new Set([userId]));
  };

  // Build a "self" pseudo-friend object so own entries render with FeedResponseCard
  const selfFriend = useMemo(() => user ? {
    userId: user.uid,
    displayName: user.displayName || 'You',
    photoURL: user.photoURL || null,
  } : null, [user]);

  // Own published entries (non-draft or at least one category individually submitted)
  const ownPublishedEntries = useMemo(() =>
    entries.filter(e => !e.isDraft || CATEGORIES.some(c => e[c.key]?.submitted)),
  [entries]);

  // All "people" in the feed = self + friends
  const allPeople = useMemo(() =>
    selfFriend ? [selfFriend, ...friends] : friends,
  [selfFriend, friends]);

  const items = useMemo(() => {
    const peopleToShow = activePeople.size > 0
      ? allPeople.filter(p => activePeople.has(p.userId))
      : allPeople;

    const keys = activeCategory ? [activeCategory] : CATEGORIES.map(c => c.key);

    return peopleToShow
      .flatMap(person => {
        const isOwn = person.userId === user?.uid;
        const personEntries = isOwn
          ? ownPublishedEntries
          : (friendEntries[person.userId] || []);

        return personEntries.flatMap(entry =>
          keys
            .filter(catKey => {
              const data = entry[catKey];
              if (!data?.response) return false;
              // For draft entries, only show individually submitted categories
              if (entry.isDraft) return data.submitted === true;
              return true;
            })
            .map(catKey => ({
              key: `${person.userId}-${entry.date}-${catKey}`,
              friend: person,
              entry,
              categoryKey: catKey,
              isOwn,
            }))
        );
      })
      .sort((a, b) => b.entry.date.localeCompare(a.entry.date));
  }, [allPeople, friendEntries, ownPublishedEntries, activeCategory, activePeople, user]);

  const hasAnyContent = friends.length > 0 || ownPublishedEntries.length > 0;

  if (!hasAnyContent) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 className={styles.heading}>Feed</h2>
        </header>
        <div className={styles.empty}>
          <p>Nothing here yet.</p>
          <p className={styles.emptySub}>
            Submit today's prompts to see your entries here. Share your invite link in the Friends tab to see friends' entries too.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.heading}>Feed</h2>
      </header>

      <FilterBar
        friends={allPeople}
        activeCategory={activeCategory}
        activePeople={activePeople}
        onCategoryChange={setActiveCategory}
        onPersonChange={handlePersonClick}
      />

      {items.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptySub}>Nothing here yet — submit today's prompts to get started.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {items.map(item => (
            <FeedResponseCard
              key={item.key}
              entry={item.entry}
              friend={item.friend}
              categoryKey={item.categoryKey}
              isOwn={item.isOwn}
              onPersonClick={activatePerson}
            />
          ))}
        </div>
      )}
    </div>
  );
}
